// CommandPalette — ⌘K / Ctrl-K global jumper.
//
// What it does:
//   - Static "Pages" jumpers — always present, always works (even when
//     the user has just logged in and no page caches are warm).
//   - Pending approvals — top 20 by `created_at DESC`, fetched on open
//     via the same shape Inbox.tsx uses.
//   - Open meeting/call action items — top 20, fetched on open via the
//     same shape ActionItems.tsx uses.
//   - Daemons — derived from the SHARED ['heartbeats'] React Query cache
//     populated by SystemStatusChip + Settings + Agents. Zero extra
//     fetch since the cache is already hydrated whenever the sidebar
//     chip is mounted (i.e. always, under AppLayout).
//
// Why we fetch on-open instead of reading other pages' caches:
//   The Inbox / ActionItems caches only exist after those pages mount.
//   A user who logs in, hits ⌘K straight away, and types "draft" would
//   get nothing under the cache-only approach. Fetching on open gives
//   the palette its own minimal but always-correct read. The query keys
//   are unique to the palette so we don't pollute the page caches.
//
// Cancellation safety:
//   React Query handles the unmount-during-fetch race. `enabled: open`
//   means queries auto-cancel as the dialog closes.
//
// Screen field audit (CLAUDE.md §19):
//   Approval label    ← `emails.subject || draft_payload.subject || '(no subject)'`
//   Approval sublabel ← `emails.from_email || sender_email || ''`
//   Action label      ← `meeting_action_items.text`
//   Action sublabel   ← humanized source label
//   Daemon label      ← `heartbeats[].daemon_name`
//   Daemon sublabel   ← classification + interval staleness
//
// Routing:
//   Approvals → `/inbox#approval=<uuid>` — Inbox.tsx already reads this
//     fragment via `pickInitialApprovalSelection` (shipped W2 for the
//     Discord deep-link). Reusing it means the palette pre-selects the
//     row instead of dumping the user on the list head.
//   Action items → `/action-items` — no deep-link wired yet; would need
//     ActionItems.tsx to grow a hash-reader, which is its own scope. We
//     navigate to the list and let cmdk's search teach the user where
//     to look. Follow-up tracked as a chip below.
//   Pages / daemons → static path.

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Bot,
  Calendar,
  CheckSquare,
  DollarSign,
  FileText,
  LayoutDashboard,
  Lightbulb,
  Mail,
  Settings as SettingsIcon,
  Shield,
  type LucideIcon,
} from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { supabase } from '@/lib/supabase';
import type { HeartbeatRow } from '@reverra/shared/heartbeat/read';

// ============================================================================
// Static page jumpers
// ============================================================================

type PageJumper = {
  id: string;
  label: string;
  sublabel: string;
  to: string;
  icon: LucideIcon;
};

const PAGE_JUMPERS: PageJumper[] = [
  {
    id: 'page:overview',
    label: 'Overview',
    sublabel: 'Today’s rollup',
    to: '/',
    icon: LayoutDashboard,
  },
  {
    id: 'page:inbox',
    label: 'Inbox',
    sublabel: 'Email drafts awaiting approval',
    to: '/inbox',
    icon: Mail,
  },
  {
    id: 'page:meetings',
    label: 'Meetings',
    sublabel: 'Upcoming briefs + past transcripts',
    to: '/meetings',
    icon: Calendar,
  },
  {
    id: 'page:agreements',
    label: 'Agreements',
    sublabel: 'Redlines + clauses',
    to: '/agreements',
    icon: FileText,
  },
  {
    id: 'page:action-items',
    label: 'Action Items',
    sublabel: 'Open items from meetings + calls',
    to: '/action-items',
    icon: CheckSquare,
  },
  {
    id: 'page:focus',
    label: 'Focus Time',
    sublabel: 'Guardian rules + reschedules',
    to: '/focus',
    icon: Shield,
  },
  {
    id: 'page:commissions',
    label: 'Commissions',
    sublabel: 'Periods + Gusto exports',
    to: '/commissions',
    icon: DollarSign,
  },
  {
    id: 'page:agents',
    label: 'Agents',
    sublabel: 'Orchestrator, sub-agents + Learning Loop',
    to: '/agents',
    icon: Bot,
  },
  {
    id: 'page:intel',
    label: 'Intel',
    sublabel: 'Brain digests + automation suggestions',
    to: '/intel',
    icon: Lightbulb,
  },
  {
    id: 'page:settings',
    label: 'Settings',
    sublabel: 'Identity, integrations, filters',
    to: '/settings',
    icon: SettingsIcon,
  },
];

// ============================================================================
// Approvals source
// ============================================================================

type PaletteApproval = {
  id: string;
  subject: string;
  fromLine: string;
};

const PALETTE_APPROVAL_SELECT =
  'id, draft_payload, emails:target_email_id (from_email, from_name, subject)';

async function fetchPaletteApprovals(): Promise<PaletteApproval[]> {
  // Phase 1: email is the only channel with an approval queue (N1).
  // Drop the .eq('agent', ...) when another agent ships approvals.
  const { data, error } = await supabase
    .from('approvals')
    .select(PALETTE_APPROVAL_SELECT)
    .eq('agent', 'email_triage')
    .eq('status', 'pending')
    .order('created_at', { ascending: false })
    .limit(20);
  if (error) throw error;
  return (data ?? []).map((row: Record<string, unknown>): PaletteApproval => {
    const email = (row.emails ?? null) as {
      from_email: string | null;
      from_name: string | null;
      subject: string | null;
    } | null;
    const draft = (row.draft_payload ?? {}) as { subject?: unknown };
    const subjectFromEmail = typeof email?.subject === 'string' ? email.subject : null;
    const subjectFromDraft = typeof draft.subject === 'string' ? draft.subject : null;
    const subject = subjectFromEmail ?? subjectFromDraft ?? '(no subject)';
    const fromLine = email?.from_name?.trim() || email?.from_email?.trim() || '';
    return { id: String(row.id), subject, fromLine };
  });
}

// ============================================================================
// Action items source
// ============================================================================

type PaletteActionItem = {
  id: string;
  text: string;
  source: 'meeting' | 'call' | 'email';
};

const PALETTE_ACTION_SELECT = 'id, text, source, status';

async function fetchPaletteActionItems(): Promise<PaletteActionItem[]> {
  const { data, error } = await supabase
    .from('meeting_action_items')
    .select(PALETTE_ACTION_SELECT)
    .eq('status', 'open')
    .order('created_at', { ascending: false })
    .limit(20);
  if (error) throw error;
  return (data ?? []).map((row: Record<string, unknown>): PaletteActionItem => {
    const sourceRaw = typeof row.source === 'string' ? row.source : 'meeting';
    const source: PaletteActionItem['source'] =
      sourceRaw === 'call' || sourceRaw === 'email' ? sourceRaw : 'meeting';
    return {
      id: String(row.id),
      text: String(row.text ?? '').trim() || '(untitled)',
      source,
    };
  });
}

// ============================================================================
// Component
// ============================================================================

export type CommandPaletteProps = {
  open: boolean;
  onOpenChange: (next: boolean) => void;
};

export default function CommandPalette({ open, onOpenChange }: CommandPaletteProps): JSX.Element {
  const navigate = useNavigate();

  const approvalsQ = useQuery({
    queryKey: ['palette', 'approvals'],
    queryFn: fetchPaletteApprovals,
    enabled: open,
    retry: 0,
    // 30s feels fast enough for an on-demand palette without re-firing
    // on every open/close cycle while the user is browsing.
    staleTime: 30_000,
  });

  const actionsQ = useQuery({
    queryKey: ['palette', 'action-items'],
    queryFn: fetchPaletteActionItems,
    enabled: open,
    retry: 0,
    staleTime: 30_000,
  });

  // Pull heartbeats from the SHARED cache. The writer is
  // SystemStatusChip (mounted in AppSidebar; refetchInterval: 30s);
  // /settings and /agents are additional writers under the same
  // queryKey. We pass an empty queryFn + enabled: false so this
  // consumer never triggers its own refetch — we're strictly
  // reading whatever cache the chip has hydrated.
  const heartbeatsQ = useQuery<HeartbeatRow[]>({
    queryKey: ['heartbeats'],
    queryFn: async () => [],
    enabled: false,
  });

  const heartbeats = heartbeatsQ.data ?? [];

  const handleSelect = (to: string): void => {
    onOpenChange(false);
    navigate(to);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Jump to a page, approval, action item, or daemon…" />
      <CommandList>
        <CommandEmpty>No matches.</CommandEmpty>

        <CommandGroup heading="Pages">
          {PAGE_JUMPERS.map((p) => {
            const Icon = p.icon;
            return (
              <CommandItem
                key={p.id}
                value={`${p.label} ${p.sublabel}`}
                onSelect={() => handleSelect(p.to)}
              >
                <Icon className="mr-2 h-4 w-4 text-muted-foreground" aria-hidden />
                <span className="flex-1">{p.label}</span>
                <span className="text-xs text-muted-foreground">{p.sublabel}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>

        {approvalsQ.data && approvalsQ.data.length > 0 ? (
          <CommandGroup heading="Pending approvals">
            {approvalsQ.data.map((a) => (
              <CommandItem
                key={a.id}
                value={`${a.subject} ${a.fromLine}`}
                onSelect={() => handleSelect(`/inbox#approval=${a.id}`)}
              >
                <Bell className="mr-2 h-4 w-4 text-muted-foreground" aria-hidden />
                <span className="flex-1 truncate">{a.subject}</span>
                {a.fromLine && (
                  <span className="text-xs text-muted-foreground truncate ml-2">{a.fromLine}</span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {actionsQ.data && actionsQ.data.length > 0 ? (
          <CommandGroup heading="Open action items">
            {actionsQ.data.map((a) => (
              <CommandItem
                key={a.id}
                value={`${a.text} ${a.source}`}
                onSelect={() => handleSelect('/action-items')}
              >
                <CheckSquare className="mr-2 h-4 w-4 text-muted-foreground" aria-hidden />
                <span className="flex-1 truncate">{a.text}</span>
                <span className="text-xs text-muted-foreground capitalize">{a.source}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {heartbeats.length > 0 ? (
          <CommandGroup heading="Daemons">
            {heartbeats.map((h) => (
              <CommandItem
                key={h.daemon_name}
                value={`daemon ${h.daemon_name}`}
                onSelect={() => handleSelect('/agents')}
              >
                <Bot className="mr-2 h-4 w-4 text-muted-foreground" aria-hidden />
                <span className="flex-1 truncate font-mono text-[12px]">{h.daemon_name}</span>
                {h.parse_error ? (
                  <span className="text-xs text-alert">parse error</span>
                ) : !h.payload ? (
                  <span className="text-xs text-muted-foreground">not reporting</span>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    every {h.payload.interval_seconds}s
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}
      </CommandList>
    </CommandDialog>
  );
}

// ============================================================================
// Global ⌘K listener hook (used by AppLayout)
// ============================================================================

export function useCommandPaletteHotkey(setOpen: (next: boolean) => void): void {
  // Deps: `[setOpen]`. React Query / React's own `useState` setter is
  // referentially stable across renders, so this effect mounts once
  // and unmounts only when the provider unmounts (i.e. on logout).
  // If a future contributor wraps `setOpen` in `useCallback`, take
  // care to keep its identity stable — otherwise the listener re-
  // attaches on every render. R2 reviewer N1.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);
}

// ============================================================================
// Tiny shared context so TopBar's Search button can open the palette
// without prop-drilling.
// ============================================================================

const CommandPaletteContext = createContext<{ open: () => void } | null>(null);

export function CommandPaletteProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [open, setOpen] = useState(false);
  useCommandPaletteHotkey(setOpen);
  const value = useMemo(() => ({ open: () => setOpen(true) }), []);
  return (
    <CommandPaletteContext.Provider value={value}>
      {children}
      <CommandPalette open={open} onOpenChange={setOpen} />
    </CommandPaletteContext.Provider>
  );
}

export function useCommandPalette(): { open: () => void } {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) {
    // Throw rather than noop: AppLayout is the only mount site, and a
    // silent noop here is exactly the lying-UI failure mode this PR
    // exists to fix. If a consumer ever appears outside the provider,
    // we want to know loudly. R1 reviewer S3.
    throw new Error('useCommandPalette must be used within a CommandPaletteProvider');
  }
  return ctx;
}
